import { type Framework } from '@toeverything/infra';

import { WorkspaceServerService } from '../cloud';
import { WorkspaceDBService } from '../db';
import { AccessibleDocCacheStore } from '../doc/stores/accessible-doc-cache';
import { WorkspaceRealtimeService } from '../editor';
import {
  PermissionSnapshotStore,
  WorkspacePermissionService,
} from '../permissions';
import { GlobalState } from '../storage';
import { WorkspaceScope, WorkspaceService } from '../workspace';
import { FolderNode } from './entities/folder-node';
import { FolderTree } from './entities/folder-tree';
import { FolderDocPrefetchService } from './services/folder-doc-prefetch';
import { OrganizeService } from './services/organize';
import { AccessibleFolderCacheStore } from './stores/accessible-folder-cache';
import { FolderStore } from './stores/folder';
import { WorkspacePresenceStore } from './stores/workspace-presence';

export { DocumentPresenceAvatarStack } from './components/document-presence-avatar-stack';
export type { FolderNode } from './entities/folder-node';
export type { PrivateFolderStorageSnapshot } from './hooks/use-private-folder-storage';
export { usePrivateFolderStorage } from './hooks/use-private-folder-storage';
export { FolderDocPrefetchService } from './services/folder-doc-prefetch';
export { OrganizeService } from './services/organize';
export type {
  AccessibleFolderMeta,
  AccessibleFolderOptimisticTrashRecord,
  AccessibleTrashedFolderMeta,
} from './stores/accessible-folder-cache';
export { AccessibleFolderCacheStore } from './stores/accessible-folder-cache';
export type {
  DocFolderBreadcrumbPath,
  DocLinkEvent,
  FolderTrashEvent,
} from './stores/folder';
export { FolderStore } from './stores/folder';
export { WorkspacePresenceStore } from './stores/workspace-presence';
export {
  type CachedFolderAreaKind,
  type CachedFolderAreaResolver,
  createCachedFolderAreaResolver,
  resolveCachedFolderArea,
} from './utils/folder-area';

export function configureOrganizeModule(framework: Framework) {
  framework
    .scope(WorkspaceScope)
    .service(OrganizeService)
    .entity(FolderTree, [FolderStore])
    .entity(FolderNode, [FolderStore])
    .store(AccessibleFolderCacheStore, [
      WorkspaceService,
      WorkspaceServerService,
      GlobalState,
      PermissionSnapshotStore,
    ])
    .store(FolderStore, [
      WorkspaceDBService,
      WorkspaceService,
      AccessibleFolderCacheStore,
      WorkspacePermissionService,
    ])
    // sl-suoi.3.1: Folder-aware doc prefetch on expand
    .service(FolderDocPrefetchService, [
      WorkspaceService,
      FolderStore,
      AccessibleDocCacheStore,
    ])
    // sl-jmyl.6: Workspace presence via Direct Socket (WorkspaceRealtimeService)
    .store(WorkspacePresenceStore, [
      AccessibleFolderCacheStore,
      AccessibleDocCacheStore,
      WorkspaceServerService,
      WorkspaceRealtimeService,
    ]);
}
